import { Box, Button, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useBoolean } from 'usehooks-ts';

import { getTaxInfo } from '@/api/tax-info';

import { Drawer } from '@/components/Layout/Drawer';
import { ReviewAndSubmitCard } from '@/components/PageForm/MoreInfoForm/ReviewAndSubmit/ReviewAndSubmitCard';

import { getAssetPath } from '@/constants/configs';

import { useAppSelector } from '@/hooks/redux';
import { useTranslate } from '@/hooks/useTranslate';

import { selectLoggedInUser } from '@/redux/auth/selectors';
import { selectAdobeSignFormFeature } from '@/redux/configs/baseSelectors';
import { selectActiveUserAccountTypes } from '@/redux/user-data/selectors';

import { isRemediationCustomer } from '@/utils/user';

import { isTaxInfoComplete } from '../drawers/TaxInfoDrawer/utils/taxInfoStatus';
import { useYourFinancesStatus } from '../YourFinances/hooks';
import {
  AccountsSection,
  AddressSection,
  EmploymentDetailsSection,
  FinancesSection,
  FundingSection,
  GovernmentRolesSection,
  PersonalDetailsSection,
  TaxSection,
} from './sections';

import { SectionsContainer } from './ReviewMyInfo.styled';

const ALL_SECTIONS = [
  'personalDetails',
  'address',
  'employmentDetails',
  'accounts',
  'funding',
  'finances',
  'tax',
  'governmentRoles',
] as const;

type SectionId = (typeof ALL_SECTIONS)[number];

type ReviewMyInfoProps = {
  disabled?: boolean;
  hideTrigger?: boolean;
  open?: boolean;
  onClose?: () => void;
};

export function ReviewMyInfo({
  disabled,
  hideTrigger = false,
  open: controlledOpen,
  onClose,
}: Readonly<ReviewMyInfoProps>) {
  const { t } = useTranslate();
  const user = useAppSelector(selectLoggedInUser);
  const {
    value: internalOpen,
    setTrue: openDrawer,
    setFalse: closeDrawer,
  } = useBoolean(false);

  const isControlled = controlledOpen !== undefined;
  const isDrawerOpen = isControlled ? controlledOpen : internalOpen;

  const handleClose = () => {
    if (isControlled) {
      onClose?.();
      return;
    }
    closeDrawer();
  };

  const isRemediation = isRemediationCustomer(user);
  const isAdobeSigningFormEnabled = useAppSelector(selectAdobeSignFormFeature);
  const currentAccounts = useAppSelector(selectActiveUserAccountTypes);
  const {
    isVisible: isYourFinancesVisible,
    isComplete: isYourFinancesComplete,
  } = useYourFinancesStatus();
  const { data: taxInfo } = useQuery({
    queryKey: ['taxInfo'],
    queryFn: getTaxInfo,
    enabled: !isAdobeSigningFormEnabled,
  });

  const isFundingCollected =
    currentAccounts.length > 0 &&
    currentAccounts.every(
      account =>
        account.fundingSourceUpdatedInRemediation &&
        account.estimatedMonthlyDepositAmountUpdatedInRemediation,
    );

  const visibleSections: SectionId[] = useMemo(() => {
    if (!isRemediation) {
      return ALL_SECTIONS.filter(id =>
        user?.isHighRisk ? true : id !== 'finances',
      );
    }

    return ALL_SECTIONS.filter(id => {
      switch (id) {
        case 'accounts':
          return false;
        case 'funding':
          return isFundingCollected;
        case 'finances':
          return isYourFinancesVisible && isYourFinancesComplete;
        case 'tax':
          return !isAdobeSigningFormEnabled && isTaxInfoComplete(taxInfo);
        default:
          return true;
      }
    });
  }, [
    user?.isHighRisk,
    isRemediation,
    isFundingCollected,
    isYourFinancesVisible,
    isYourFinancesComplete,
    isAdobeSigningFormEnabled,
    taxInfo,
  ]);

  return (
    <>
      {!hideTrigger && (
        <Box textAlign="right" mt={0.5}>
          <Button
            variant="outlined"
            fullWidth
            onClick={openDrawer}
            disabled={disabled}
          >
            {t('moreInfo.reviewMyInformation.button')}
          </Button>
        </Box>
      )}
      <Drawer
        anchor="right"
        open={isDrawerOpen}
        onClose={handleClose}
        title={t('moreInfo.reviewMyInformation.drawerTitle')}
        headerBgColor={theme => theme.palette.neutral[100]}
        bgColor={theme => theme.palette.neutral[100]}
      >
        <Stack direction="column">
          <Stack>
            <Box
              component="img"
              src={getAssetPath('/assets/images/icon-review.svg')}
              alt="icon-review"
              sx={{ height: 128, width: 'auto', my: 4 }}
            />
          </Stack>

          <Typography variant="headlineH3" textAlign="center" mb={1}>
            {t('moreInfo.reviewMyInformation.title')}
          </Typography>
          <Typography
            variant="bodyB2Regular"
            textAlign="center"
            color="neutral.800"
            mb={4}
          >
            {t('moreInfo.reviewMyInformation.subtitle')}
          </Typography>
        </Stack>
        <SectionsContainer>
          {visibleSections.map((id, index) => (
            <SectionRenderer
              key={id}
              id={id}
              currentIndex={index + 1}
              totalSections={visibleSections.length}
            />
          ))}
          {!isAdobeSigningFormEnabled && (
            <ReviewAndSubmitCard disabled={disabled} />
          )}
        </SectionsContainer>
      </Drawer>
    </>
  );
}

type SectionRendererProps = {
  id: SectionId;
  currentIndex: number;
  totalSections: number;
};

function SectionRenderer({
  id,
  currentIndex,
  totalSections,
}: Readonly<SectionRendererProps>) {
  const props = { currentIndex, totalSections };
  switch (id) {
    case 'personalDetails':
      return <PersonalDetailsSection {...props} />;
    case 'address':
      return <AddressSection {...props} />;
    case 'employmentDetails':
      return <EmploymentDetailsSection {...props} />;
    case 'accounts':
      return <AccountsSection {...props} />;
    case 'funding':
      return <FundingSection {...props} />;
    case 'finances':
      return <FinancesSection {...props} />;
    case 'tax':
      return <TaxSection {...props} />;
    case 'governmentRoles':
      return <GovernmentRolesSection {...props} />;
    default:
      return <></>;
  }
}
